import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Briefcase, Plus, Loader2, AlertCircle } from "lucide-react";
import AppShell from "../components/Layout/AppShell";
import api from "../lib/api";
import { useAuth } from "../lib/auth-context";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";

const STROKE = 1.5;

function NewRoleDialog({ open, onOpenChange, onCreated }) {
  const [title, setTitle] = useState("");
  const [department, setDepartment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setSubmitting(true);
    try {
      const { data } = await api.post("/roles", { title, department: department || undefined });
      onCreated(data);
      setTitle(""); setDepartment("");
      onOpenChange(false);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to add role");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="new-role-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-medium">New role</DialogTitle>
          <DialogDescription className="text-[13px] text-ink-500">
            A role groups candidates and interviews by what you&rsquo;re hiring for.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 py-2" data-testid="new-role-form">
          <div>
            <label className="text-[12px] font-medium uppercase tracking-wider text-ink-500">Title</label>
            <input required className="hp-input mt-1.5" placeholder="e.g. Senior Backend Engineer"
                   value={title} onChange={(e) => setTitle(e.target.value)} data-testid="role-title" />
          </div>
          <div>
            <label className="text-[12px] font-medium uppercase tracking-wider text-ink-500">Department (optional)</label>
            <input className="hp-input mt-1.5" placeholder="e.g. Engineering"
                   value={department} onChange={(e) => setDepartment(e.target.value)} data-testid="role-department" />
          </div>
          {error && (
            <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-[13px] text-red-700" data-testid="role-error">
              <AlertCircle className="mt-0.5 h-4 w-4" strokeWidth={STROKE} /> {error}
            </div>
          )}
          <DialogFooter>
            <button type="button" onClick={() => onOpenChange(false)} className="hp-btn-ghost">Cancel</button>
            <button type="submit" disabled={submitting} className="hp-btn-primary" data-testid="role-submit">
              {submitting && <Loader2 className="h-4 w-4 animate-spin" strokeWidth={STROKE} />}
              Add role
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Roles() {
  const { member } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const navigate = useNavigate();
  const canCreate = ["admin", "recruiter"].includes(member?.role);

  const load = () => {
    setLoading(true);
    api.get("/roles").then(({ data }) => setItems(data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  return (
    <AppShell>
      <div data-testid="roles-page" className="space-y-8">
        <header className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <div className="hp-section-label">Roles</div>
            <h1 className="font-display mt-2 text-3xl font-medium tracking-tight text-ink-900">Open roles</h1>
            <p className="mt-1 text-[14px] text-ink-500">Click a role to see the candidate pipeline by round.</p>
          </div>
          {canCreate && (
            <button onClick={() => setDialogOpen(true)} className="hp-btn-primary" data-testid="new-role-btn">
              <Plus className="h-4 w-4" strokeWidth={STROKE} /> New role
            </button>
          )}
        </header>

        {loading && (
          <div className="hp-card flex justify-center p-12">
            <Loader2 className="h-5 w-5 animate-spin text-ink-400" strokeWidth={STROKE} />
          </div>
        )}

        {!loading && items.length === 0 && (
          <div className="hp-card flex flex-col items-center justify-center px-6 py-16 text-center" data-testid="roles-empty">
            <Briefcase className="h-6 w-6 text-ink-300" strokeWidth={STROKE} />
            <h2 className="font-display mt-4 text-xl font-medium text-ink-900">No roles yet</h2>
            <p className="mt-2 max-w-md text-[14px] text-ink-500">Add a role to organise interviews and pipeline views.</p>
            {canCreate && (
              <button onClick={() => setDialogOpen(true)} className="hp-btn-primary mt-6" data-testid="roles-empty-cta">
                <Plus className="h-4 w-4" strokeWidth={STROKE} /> Add the first role
              </button>
            )}
          </div>
        )}

        {!loading && items.length > 0 && (
          <div className="hp-card overflow-hidden">
            <table className="w-full" data-testid="roles-table">
              <thead className="bg-ink-50 border-b border-ink-200">
                <tr className="text-left text-[11px] font-medium uppercase tracking-wider text-ink-500">
                  <th className="px-5 py-3">Title</th>
                  <th className="px-5 py-3">Department</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Opened</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {items.map((r) => (
                  <tr key={r.role_id} onClick={() => navigate(`/roles/${r.role_id}`)}
                      className="hover:bg-ink-50 transition cursor-pointer"
                      data-testid={`role-row-${r.role_id}`}>
                    <td className="px-5 py-3 text-[14px] font-medium text-ink-900">{r.title}</td>
                    <td className="px-5 py-3 text-[13px] text-ink-700">{r.department || "—"}</td>
                    <td className="px-5 py-3 text-[13px] text-ink-700 capitalize">{r.status}</td>
                    <td className="px-5 py-3 text-[12px] text-ink-500">{new Date(r.created_at).toLocaleDateString()}</td>
                    <td className="px-5 py-3 text-right text-[13px] text-emerald-800">Open →</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <NewRoleDialog open={dialogOpen} onOpenChange={setDialogOpen} onCreated={() => load()} />
    </AppShell>
  );
}
